import React, { useEffect, useState, lazy, Suspense } from "react";
import { StyledBox } from "../../../../styled-mui";
import "keen-slider/keen-slider.min.css";
import { KeenSliderPlugin, useKeenSlider } from "keen-slider/react";
import { StyledKeenSlider } from "../../keen-slider/styled-keenSlider";
import KeenSliderArrow from "../../keen-slider/KeenSliderArrow";
import AsyncCall from "../../../../_foundation/gtm/async.service";
// import { useSite } from "../../../../_foundation/hooks/useSite";

const AdviceCard = lazy(() => import("./AdviceCard/AdviceCard"));
const ResizePlugin: KeenSliderPlugin = (slider) => {
  const observer = new ResizeObserver(function () {
    slider.update();
  });

  slider.on("created", () => {
    observer.observe(slider.container);
  });
  slider.on("destroyed", () => {
    observer.unobserve(slider.container);
  });
};

interface AdviceSliderProps {
  data: any[];
  homePage?: boolean;
  isMenu?: boolean;
}

/**
 * AdviceSlider component
 *
 * @param {Array} data Fetched posts data
 */
const AdviceSlider: React.FC<AdviceSliderProps> = ({ data, homePage, isMenu= false }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [currentMaxIdx, setCurrentMaxIdx] = useState(0);
  // const { mySite } = useSite();

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      optionsChanged(slider) {
        setCurrentMaxIdx(slider.track?.details?.maxIdx);
      },
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
      created(slider) {
        setCurrentMaxIdx(slider.track?.details?.maxIdx);
        setLoaded(true);
      },
      slides: {
        perView: 3,
        spacing: 20,
        origin: "auto",
      },
      breakpoints: {
        "(max-width: 767px)": {
          slides: { perView: 1.3, spacing: 20 },
        },
      },
    },
    [ResizePlugin]
  );

  useEffect(() => {
    if (data) {
      instanceRef.current?.update(undefined, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <>
      <StyledBox ref={sliderRef} className="keen-slider overflow-visible">
        {data &&
          data.map((item, index) => (
            <StyledBox className="keen-slider__slide" key={index}>
              <Suspense fallback={null}>
                <AdviceCard content={item} isMenu={isMenu}/>
              </Suspense>
            </StyledBox>
          ))}
      </StyledBox>

      {loaded && instanceRef.current && currentMaxIdx > 0 && (
        <StyledKeenSlider>
          <StyledBox className="dots" pt={10} mx={{ xs: 0, sm: "auto" }} sx={{ width: { sm: "25%" } }}>
            <KeenSliderArrow
              color={homePage ? "text.primary" : "white.main"}
              left
              onClick={(e: any) => {
                e.stopPropagation() || instanceRef.current?.prev();
                if (currentSlide !== 0) {
                  AsyncCall.sendPageClickSliderEvent({
                    slider_name: "Advices",
                    slider_position: currentSlide - 1,
                  });
                }
              }}
              disabled={currentSlide === 0}
            />
            {[...Array(instanceRef.current.track.details.maxIdx + 1).keys()].map((idx) => {
              return (
                <StyledBox
                  aria-label="Carousel navigation"
                  component="button"
                  key={idx}
                  onClick={() => {
                    instanceRef.current?.moveToIdx(idx);
                  }}
                  className={"dot" + (currentSlide === idx ? " active" : "")}
                  sx={{
                    borderColor: homePage ? "text.primary" : "white.main",
                    bgcolor: "transparent",
                    "&.active": { bgcolor: homePage ? "text.primary" : "white.main" },
                  }}></StyledBox>
              );
            })}
            <KeenSliderArrow
              color={homePage ? "text.primary" : "white.main"}
              onClick={(e: any) => {
                e.stopPropagation() || instanceRef.current?.next();
                if (currentSlide !== currentMaxIdx) {
                  AsyncCall.sendPageClickSliderEvent({
                    slider_name: "Advices",
                    slider_position: currentSlide + 1,
                  });
                }
              }}
              disabled={currentSlide === instanceRef.current.track.details.slides.length - 1}
            />
          </StyledBox>
        </StyledKeenSlider>
      )}
    </>
  );
};

export default AdviceSlider;
